import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRight,
  Users,
  Euro,
  ShoppingCart,
  TrendingUp,
  Settings,
  Play,
  CheckCircle,
  Clock,
  AlertTriangle,
  Trash2,
  Edit,
  Plus,
  X,
  History,
  Target,
  Zap,
} from 'lucide-react'
import { groupPromotions, PromotionRule, PromotionCandidate, PromotionHistory } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useToast } from '@/components/ui/Toast'
import CustomerDetailModal from './CustomerDetailModal'

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

function RuleCard({
  rule,
  onEdit,
  onDelete,
  onViewCandidates,
  isDeleting,
}: {
  rule: PromotionRule
  onEdit: () => void
  onDelete: () => void
  onViewCandidates: () => void
  isDeleting: boolean
}) {
  return (
    <div className={cn(
      'card p-4 border-l-4 transition-all hover:shadow-md',
      rule.active ? 'border-l-primary-500' : 'border-l-slate-300 opacity-60'
    )}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-slate-900 dark:text-white">{rule.name}</h3>
            {rule.auto_apply && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                <Zap className="w-3 h-3 inline mr-1" />
                Auto
              </span>
            )}
            {!rule.active && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600">
                Inactiva
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-3">
            <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded font-medium">
              {rule.from_group_name}
            </span>
            <ArrowRight className="w-4 h-4" />
            <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded font-medium">
              {rule.to_group_name}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 dark:text-slate-400">
                Min. {rule.min_orders} pedidos
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Euro className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 dark:text-slate-400">
                Min. {rule.min_revenue.toLocaleString('es-ES')}€
              </span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600 dark:text-slate-400">
                Ticket {rule.min_avg_order_value.toLocaleString('es-ES')}€
              </span>
            </div>
          </div>

          {rule.description && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              {rule.description}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={onViewCandidates}
            className="p-2 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
            title="Ver candidatos"
          >
            <Users className="w-5 h-5" />
          </button>
          <button
            onClick={onEdit}
            className="p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            title="Editar regla"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={onDelete}
            disabled={isDeleting}
            className="p-2 text-danger-600 hover:bg-danger-100 dark:hover:bg-danger-900/30 rounded-lg transition-colors disabled:opacity-50"
            title="Eliminar regla"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function CandidateRow({
  candidate,
  onApply,
  onViewProfile,
  isApplying,
}: {
  candidate: PromotionCandidate
  onApply: () => void
  onViewProfile: () => void
  isApplying: boolean
}) {
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
      <td className="px-4 py-3">
        <button
          onClick={onViewProfile}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          {candidate.name}
        </button>
        <p className="text-xs text-slate-500">{candidate.email}</p>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {candidate.company || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {candidate.current_group_name}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
        {candidate.total_orders}
      </td>
      <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-white">
        {candidate.total_revenue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {candidate.avg_order_value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
      </td>
      <td className="px-4 py-3">
        <button
          onClick={onApply}
          disabled={isApplying}
          className="btn btn-primary btn-sm"
        >
          <ArrowRight className="w-4 h-4 mr-1" />
          {isApplying ? 'Aplicando...' : `Promover a ${candidate.target_group_name}`}
        </button>
      </td>
    </tr>
  )
}

function RuleFormModal({
  rule,
  onClose,
  onSave,
  isSaving,
}: {
  rule: PromotionRule | null
  onClose: () => void
  onSave: (data: Partial<PromotionRule>) => void
  isSaving: boolean
}) {
  const [formData, setFormData] = useState<Partial<PromotionRule>>({
    name: rule?.name || '',
    description: rule?.description || '',
    from_group_id: rule?.from_group_id || 3,
    to_group_id: rule?.to_group_id || 4,
    min_orders: rule?.min_orders || 3,
    min_revenue: rule?.min_revenue || 500,
    min_revenue_period_months: rule?.min_revenue_period_months || 12,
    min_avg_order_value: rule?.min_avg_order_value || 100,
    auto_apply: rule?.auto_apply || false,
    notify_customer: rule?.notify_customer ?? true,
    active: rule?.active ?? true,
  })

  const groups = [
    { id: 3, label: 'Cliente PVP' },
    { id: 4, label: 'Distribuidor' },
    { id: 6, label: 'VIP' },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-lg">
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              {rule ? 'Editar Regla' : 'Nueva Regla de Promoción'}
            </h2>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Nombre de la regla *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Descripción
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Grupo origen
                </label>
                <select
                  value={formData.from_group_id}
                  onChange={(e) => setFormData({ ...formData, from_group_id: Number(e.target.value) })}
                  className="input w-full"
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Grupo destino
                </label>
                <select
                  value={formData.to_group_id}
                  onChange={(e) => setFormData({ ...formData, to_group_id: Number(e.target.value) })}
                  className="input w-full"
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Min. pedidos
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_orders}
                  onChange={(e) => setFormData({ ...formData, min_orders: Number(e.target.value) })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Min. facturación (€)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_revenue}
                  onChange={(e) => setFormData({ ...formData, min_revenue: Number(e.target.value) })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Min. ticket (€)
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.min_avg_order_value}
                  onChange={(e) => setFormData({ ...formData, min_avg_order_value: Number(e.target.value) })}
                  className="input w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Período de análisis (meses)
              </label>
              <input
                type="number"
                min="1"
                max="36"
                value={formData.min_revenue_period_months}
                onChange={(e) => setFormData({ ...formData, min_revenue_period_months: Number(e.target.value) })}
                className="input w-full"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_apply}
                  onChange={(e) => setFormData({ ...formData, auto_apply: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Aplicar promociones automáticamente
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.notify_customer}
                  onChange={(e) => setFormData({ ...formData, notify_customer: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Notificar al cliente por email
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Regla activa
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button type="button" onClick={onClose} className="btn btn-secondary">
                Cancelar
              </button>
              <button type="submit" disabled={isSaving} className="btn btn-primary">
                {isSaving ? 'Guardando...' : rule ? 'Guardar Cambios' : 'Crear Regla'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function GroupPromotionsTab() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [activeView, setActiveView] = useState<'rules' | 'candidates' | 'history'>('rules')
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null)
  const [editingRule, setEditingRule] = useState<PromotionRule | null>(null)
  const [showNewRuleModal, setShowNewRuleModal] = useState(false)
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState<string | null>(null)

  // Queries
  const rulesQuery = useQuery({
    queryKey: ['promotion-rules'],
    queryFn: () => groupPromotions.getRules(false),
  })

  const candidatesQuery = useQuery({
    queryKey: ['promotion-candidates', selectedRuleId],
    queryFn: () => groupPromotions.getCandidates(selectedRuleId || undefined),
    enabled: activeView === 'candidates',
  })

  const historyQuery = useQuery({
    queryKey: ['promotion-history'],
    queryFn: () => groupPromotions.getHistory(undefined, 100),
    enabled: activeView === 'history',
  })

  // Mutations
  const createRuleMutation = useMutation({
    mutationFn: (data: Partial<PromotionRule>) => groupPromotions.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-rules'] })
      setShowNewRuleModal(false)
      toast.success('Regla creada correctamente')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const updateRuleMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PromotionRule> }) =>
      groupPromotions.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-rules'] })
      setEditingRule(null)
      toast.success('Regla actualizada correctamente')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const deleteRuleMutation = useMutation({
    mutationFn: (ruleId: number) => groupPromotions.deleteRule(ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotion-rules'] })
      toast.success('Regla eliminada')
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const applyPromotionMutation = useMutation({
    mutationFn: ({ email, ruleId }: { email: string; ruleId: number }) =>
      groupPromotions.applyPromotion(email, ruleId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['promotion-candidates'] })
      queryClient.invalidateQueries({ queryKey: ['promotion-history'] })
      toast.success('Promoción aplicada', data.message)
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const runAutoMutation = useMutation({
    mutationFn: () => groupPromotions.runAutoPromotions(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['promotion-candidates'] })
      queryClient.invalidateQueries({ queryKey: ['promotion-history'] })
      toast.success('Completado', `${data.promotions_applied} promociones aplicadas`)
    },
    onError: (error: any) => {
      toast.error('Error', error.response?.data?.detail || error.message)
    },
  })

  const handleViewCandidates = (ruleId: number) => {
    setSelectedRuleId(ruleId)
    setActiveView('candidates')
  }

  const views = [
    { id: 'rules' as const, label: 'Reglas', icon: Settings },
    { id: 'candidates' as const, label: 'Candidatos', icon: Users },
    { id: 'history' as const, label: 'Historial', icon: History },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {views.map((view) => {
            const Icon = view.icon
            return (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                  activeView === view.id
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700'
                )}
              >
                <Icon className="w-4 h-4" />
                {view.label}
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2">
          {activeView === 'rules' && (
            <button
              onClick={() => setShowNewRuleModal(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Regla
            </button>
          )}
          {activeView === 'candidates' && (
            <button
              onClick={() => runAutoMutation.mutate()}
              disabled={runAutoMutation.isPending}
              className="btn btn-success"
            >
              <Play className={cn('w-4 h-4 mr-2', runAutoMutation.isPending && 'animate-spin')} />
              {runAutoMutation.isPending ? 'Ejecutando...' : 'Ejecutar Auto-Promociones'}
            </button>
          )}
        </div>
      </div>

      {/* Rules View */}
      {activeView === 'rules' && (
        <div className="space-y-4">
          {rulesQuery.isLoading ? (
            <div className="text-center py-8 text-slate-500">Cargando reglas...</div>
          ) : rulesQuery.data?.length === 0 ? (
            <div className="card p-8 text-center">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No hay reglas configuradas
              </h3>
              <p className="text-slate-500 mb-4">
                Crea reglas para promocionar clientes automáticamente entre grupos
              </p>
              <button onClick={() => setShowNewRuleModal(true)} className="btn btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Crear Primera Regla
              </button>
            </div>
          ) : (
            rulesQuery.data?.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                onEdit={() => setEditingRule(rule)}
                onDelete={() => {
                  if (confirm('¿Eliminar esta regla?')) {
                    deleteRuleMutation.mutate(rule.id)
                  }
                }}
                onViewCandidates={() => handleViewCandidates(rule.id)}
                isDeleting={deleteRuleMutation.isPending}
              />
            ))
          )}
        </div>
      )}

      {/* Candidates View */}
      {activeView === 'candidates' && (
        <div>
          {/* Rule Filter */}
          <div className="mb-4">
            <select
              value={selectedRuleId || ''}
              onChange={(e) => setSelectedRuleId(e.target.value ? Number(e.target.value) : null)}
              className="input"
            >
              <option value="">Todas las reglas</option>
              {rulesQuery.data?.filter(r => r.active).map((rule) => (
                <option key={rule.id} value={rule.id}>{rule.name}</option>
              ))}
            </select>
          </div>

          {candidatesQuery.isLoading ? (
            <div className="text-center py-8 text-slate-500">Buscando candidatos...</div>
          ) : candidatesQuery.data?.length === 0 ? (
            <div className="card p-8 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-success-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No hay candidatos pendientes
              </h3>
              <p className="text-slate-500">
                Todos los clientes elegibles ya han sido promovidos
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Empresa
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Grupo Actual
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Pedidos
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Facturación
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Ticket Medio
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {candidatesQuery.data?.map((candidate) => (
                    <CandidateRow
                      key={`${candidate.email}-${candidate.rule_id}`}
                      candidate={candidate}
                      onApply={() => applyPromotionMutation.mutate({
                        email: candidate.email,
                        ruleId: candidate.rule_id
                      })}
                      onViewProfile={() => setSelectedCustomerEmail(candidate.email)}
                      isApplying={applyPromotionMutation.isPending}
                    />
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800 text-sm text-slate-500">
                {candidatesQuery.data?.length} candidatos encontrados
              </div>
            </div>
          )}
        </div>
      )}

      {/* History View */}
      {activeView === 'history' && (
        <div>
          {historyQuery.isLoading ? (
            <div className="text-center py-8 text-slate-500">Cargando historial...</div>
          ) : historyQuery.data?.length === 0 ? (
            <div className="card p-8 text-center">
              <Clock className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                Sin historial de promociones
              </h3>
              <p className="text-slate-500">
                Aquí aparecerán las promociones aplicadas
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Fecha
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Promoción
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Regla
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      Aplicado por
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {historyQuery.data?.map((entry) => (
                    <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {new Date(entry.applied_at).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedCustomerEmail(entry.customer_email)}
                          className="text-sm font-medium text-primary-600 hover:text-primary-700"
                        >
                          {entry.customer_name || entry.customer_email}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-slate-600 dark:text-slate-400">
                            {entry.from_group_name}
                          </span>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-primary-600 dark:text-primary-400">
                            {entry.to_group_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                        {entry.rule_name || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          'px-2 py-0.5 text-xs font-medium rounded-full',
                          entry.applied_by === 'auto'
                            ? 'bg-success-100 text-success-700'
                            : 'bg-slate-100 text-slate-600'
                        )}>
                          {entry.applied_by === 'auto' ? 'Automático' : 'Manual'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {(showNewRuleModal || editingRule) && (
        <RuleFormModal
          rule={editingRule}
          onClose={() => {
            setShowNewRuleModal(false)
            setEditingRule(null)
          }}
          onSave={(data) => {
            if (editingRule) {
              updateRuleMutation.mutate({ id: editingRule.id, data })
            } else {
              createRuleMutation.mutate(data)
            }
          }}
          isSaving={createRuleMutation.isPending || updateRuleMutation.isPending}
        />
      )}

      {selectedCustomerEmail && (
        <CustomerDetailModal
          email={selectedCustomerEmail}
          onClose={() => setSelectedCustomerEmail(null)}
        />
      )}
    </div>
  )
}
